import React from 'react';
import { Link } from 'react-router-dom';
import { SKILL_ROUTE_BASE } from './priorities-ai-design/designSkills';
import { BarRow, Card, Chip, DesignLanguageStyles, Mark } from './designLanguageParts';

/*
 * Legacy design pages — everything deprecated from the design language, on one
 * page of its own, linked from the Archive (owner, 2026-10-02). Moved verbatim
 * from the foot of /elevation/design-language, which now only points here.
 */

/* Every deprecated page, grouped. Each carries the Deprecated banner at its top. */
const LEGACY_GROUPS: { title: string; note: string; links: [string, string][] }[] = [
  {
    title: 'Earlier brand and design system pages',
    note: 'The consulting-era system. Replaced by the design language page and its references.',
    links: [
      ['/elevation/brand-design-system', 'Brand design system'],
      ['/elevation/comprehensive-visual-identity-system', 'Visual identity system'],
      ['/elevation/brand-elements', 'Brand elements'],
      ['/elevation/icon-library', 'Icon library (earlier)'],
      ['/elevation/component-library', 'Component library (earlier)'],
      ['/elevation/layout-grid-system', 'Layout & grid (earlier)'],
      ['/elevation/motion-animation-principles', 'Motion'],
      ['/elevation/infographic-data-viz-standards', 'Data viz (earlier)'],
      ['/elevation/application-templates', 'Application templates'],
      ['/elevation/accessibility-rules', 'Accessibility rules'],
    ],
  },
  {
    title: 'Earlier topic pages',
    note: 'Older writing on voice, pattern and grammar. The grammar is now the Grammar specification.',
    links: [
      ['/elevation/patterns-background-elements', 'Patterns'],
      ['/elevation/tone-voice-system', 'Tone & voice'],
      ['/elevation/visual-design-grammar', 'Visual grammar'],
      ['/elevation/visual-grammar-system', 'Visual grammar system'],
      ['/elevation/visual-verbal-integration', 'Visual-verbal integration'],
      ['/elevation/visual-inspiration-direction', 'Visual inspiration'],
      ['/elevation/best-practices-design-principles', 'Best practices'],
    ],
  },
  {
    title: 'Earlier product notes',
    note: 'Short notes from before the current design language. Some of their advice is now wrong.',
    links: [
      ['/elevation/priorities-ai-design-language/brand-elements', 'Brand elements · product'],
      ['/elevation/priorities-ai-design-language/component-library', 'Component library · product'],
      ['/elevation/priorities-ai-design-language/visual-design-grammar', 'Visual grammar · product'],
      ['/elevation/priorities-ai-design-language/patterns-background-elements', 'Patterns · product'],
      ['/elevation/priorities-ai-design-language/tone-voice-system', 'Tone & voice · product'],
      ['/elevation/priorities-ai-design-language/best-practices-design-principles', 'Principles · product'],
    ],
  },
  {
    title: 'Diagram pages before the current colors',
    note: 'Frozen copies of the diagram references as they were before 29 August 2026.',
    links: [
      ['/visual-language/archive/pre-v3/field-guide.html', 'Field guide (earlier)'],
      ['/visual-language/archive/pre-v3/grammar-spec.html', 'Grammar spec (earlier)'],
      ['/visual-language/archive/pre-v3/kit.html', 'Kit (earlier)'],
      ['/visual-language/archive/pre-v3/viewer.html', 'Viewer (earlier)'],
      ['/visual-language/archive/pre-v3/catchball-guide.html', 'Catchball guide (earlier)'],
    ],
  },
];

const NAV_LINKS: [string, string][] = [
  ['/elevation/archive', '← Archive'],
  ['/elevation/design-language', 'The design language →'],
];

export const DesignLanguageLegacy: React.FC = () => (
  <div className="dl3 -m-6 min-h-screen px-6 pb-0 pt-2 sm:-m-8 sm:px-10">
    <DesignLanguageStyles />
    <div className="mx-auto max-w-5xl">
      <div className="flex items-center justify-between py-5">
        {NAV_LINKS.map(([to, label]) => (
          <Link key={to} to={to} className="eyebrow text-[13px] transition-colors hover:!text-[var(--p-ink)]">
            {label}
          </Link>
        ))}
      </div>

      <section className="py-8">
        <div
          className="mb-8 border-l-[6px] bg-[#F7ECE7] px-6 py-5"
          // Inline: `.dark * { border-color }` in index.css comes after Tailwind's plain classes and would grey one.
          style={{ borderLeftColor: '#A85443' }}
        >
          <p className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="bg-[#A85443] px-2.5 py-1 font-mono text-[14px] font-bold uppercase tracking-[0.2em] text-[#FAFAF7]">Deprecated</span>
            <span className="font-mono text-[13px] uppercase tracking-[0.2em] text-[#3E4A63]">Archive &nbsp;·&nbsp; Legacy</span>
          </p>
          <h1 className="mt-3 font-serif text-[clamp(26px,3.2vw,34px)] font-normal leading-[1.18] tracking-[-0.01em] text-[#1E2A44]">
            Everything below is deprecated. Do not use it for new work.
          </h1>
          <p className="mt-3 max-w-[66ch] font-body text-[15px] leading-relaxed text-[#3E4A63]">
            These pages are kept only as a record. Each one says so at the top. The current system is on
            the design language page; nothing legacy is there.
          </p>
        </div>

        <div className="border border-[var(--p-hairline)]">
          <BarRow bar="#A85443" term="Legacy palette" def="Older colors the app's code still uses — the sage green family, Electric Mustard, Luminous Coral, Alpine. Not for new work, and not to be deleted while the app depends on them. Listed on the Legacy colors and marks page." />
          <BarRow bar="#A85443" term="Earlier logo" def="The Peak Priorities lockup with the name in title case. Retired 2026-10-01. On the same page." />
          <BarRow bar="#A85443" term="Earlier agent skill" def="priorities-ai-app-design describes the v1 look of the app. Deprecated: a record of how older screens were built, not a guide." />
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Card to="/elevation/priorities-ai-design-language/legacy" eye="Deprecated" title="Legacy colors and marks"
            desc="The first palette, its ramps, Granite, Electric Mustard, Luminous Coral, and the earlier logo. One page, so nothing legacy sits inside a current one." />
          <Card to={`${SKILL_ROUTE_BASE}/priorities-ai-app-design`} eye="Deprecated · agent skill" title="priorities-ai-app-design"
            desc="The v1 app skill and its twelve reference files, each marked deprecated." />
        </div>

        {LEGACY_GROUPS.map((g) => (
          <div key={g.title} className="mt-10">
            <h3 className="font-serif text-[21px] font-normal text-[var(--p-ink)]">{g.title}</h3>
            <p className="mt-1 max-w-[62ch] font-body text-[14.5px] leading-relaxed text-[var(--p-ink-soft)]">{g.note}</p>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {g.links.map(([to, label]) => (
                <Chip key={to} to={to} label={label} external={to.startsWith('/visual-language/') || to.startsWith('/archive')} />
              ))}
            </div>
          </div>
        ))}
      </section>

      <footer className="flex items-center justify-between border-t border-[var(--p-hairline)] py-6">
        <span className="eyebrow text-[13px]">Peak Priorities LLC &nbsp;|&nbsp; priorities.ai</span>
        <Mark height={16} />
        <span className="eyebrow text-[13px]">Legacy design pages</span>
      </footer>
    </div>
  </div>
);
